'use client';
import classNames from 'classnames';
import { usePathname } from '@/navigation';

import { TW_PRODUCT_MERCH_CARD_CLASSES } from '@/core/constants';
import { LuvImage } from '@/components/ui';
import Heading, {
  HeadingSize,
  HeadingTag,
} from '@/components/ui/Heading/Heading';
import { PRODUCT_EVENT_TYPES, trackProductEvent } from '@/utils/rudderstack';

import { InternalLinkWrapper } from '../../InternalLinkWrapper';
import { ExtendedProductMerchCardProps } from '../ProductMerchCard';

import styles from '../ProductMerchCard.module.scss';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { slugBuilder } from '@/core/contentfulConfig';

function DefaultCard({
  product,
  includes,
  includesLabel,
  productWithAddonsPricePrefix,
  pdpLink,
  backgroundTint,
  deliveredTogether,
  smallerDeliveries,
  smallerDeliveriesUnit,
  subscriptionDeliveryPrice,
}: ExtendedProductMerchCardProps) {
  const pathname = usePathname();

  const {
    featuredImage,
    title,
    shortDescription,
    productAddOns,
    shopifyProduct,
    ageRange,
  } = product.fields;

  const { 'sub-squad-enable-tabs-on-rss-pdp': enableTabs } = useFlags();

  const dividerClasses = 'border-t-gray-400 border-t-[1px]';

  const productHasAddons = productAddOns && productAddOns.length > 0;

  let pricingCopyMaybeWithPrefix = '';
  if (shopifyProduct) {
    const productPrice = shopifyProduct.variants[0].formattedPrice || '';
    pricingCopyMaybeWithPrefix =
      productHasAddons && productWithAddonsPricePrefix
        ? `${productWithAddonsPricePrefix} ${productPrice}`
        : productPrice;
  }

  const boxWithPrefixMock =
    subscriptionDeliveryPrice && smallerDeliveriesUnit
      ? `$${subscriptionDeliveryPrice}/${smallerDeliveriesUnit}`
      : undefined;

  const onProductClick = () => {
    const payload = {
      url: pathname || '',
      destinationUrl: slugBuilder(pdpLink) || '',
      productId: shopifyProduct?.id || '',
      sku: shopifyProduct?.variants[0].sku || '',
      category: shopifyProduct?.productType || '',
      name: title,
      ageRange: ageRange || '',
      ageUnits: '',
      price: shopifyProduct?.variants[0].price?.amount || 0,
      quantity: 1,
    };

    if (shopifyProduct) {
      trackProductEvent(PRODUCT_EVENT_TYPES.PRODUCT_CLICKED, payload);
    }
  };

  return (
    <InternalLinkWrapper
      link={pdpLink}
      classes={classNames(
        'rounded bg-white drop-shadow-md flex',
        TW_PRODUCT_MERCH_CARD_CLASSES.container,
        styles.productMerchCard,
      )}
      onClick={onProductClick}
    >
      <figure className="rounded-t">
        <LuvImage
          height={240}
          width={360}
          imageData={featuredImage}
          alt={featuredImage?.fields?.altText || ''}
          className={classNames(
            'w-full rounded-t',
            TW_PRODUCT_MERCH_CARD_CLASSES.image,
            `bg-${backgroundTint?.fields.color}`,
          )}
        />
      </figure>
      <div className="flex grow flex-col mx-4 md:mx-8 my-4">
        <Heading
          tag={HeadingTag.H3}
          size={HeadingSize.H3}
          text={title}
          align="left"
        />
        <p className="text-md text-left mt-3 mb-3 text-base">
          {shortDescription}
        </p>
        {includes && (
          <div
            className="pb-5 md:min-h-[272px]"
            data-testid="product-includes-container"
          >
            <p className="text-left font-bold mb-1">{includesLabel}</p>
            {includes.map((iconAndShortDescription) => (
              <div
                className="mt-2 flex gap-3 items-start"
                key={iconAndShortDescription.sys.id}
              >
                <LuvImage
                  height={18}
                  width={18}
                  imageData={iconAndShortDescription.fields.image}
                  className="flex-none mt-2"
                  alt={iconAndShortDescription.fields.image.fields?.altText}
                />
                <p className="text-lg leading-normal">
                  {iconAndShortDescription.fields.description}
                </p>
              </div>
            ))}
          </div>
        )}
        {shopifyProduct &&
          (enableTabs === true ? (
            <div className={classNames('mt-auto pt-4', dividerClasses)}>
              <div className="flex items-center justify-between">
                <p className="text-lg">{deliveredTogether}</p>
                <Heading
                  tag={HeadingTag.H4}
                  size={HeadingSize.H4}
                  text={pricingCopyMaybeWithPrefix}
                  align="left"
                  isFullWidth={false}
                />
              </div>
              <div className="flex items-center justify-between">
                <p className="text-lg">{smallerDeliveries}</p>
                <Heading
                  tag={HeadingTag.H4}
                  size={HeadingSize.H4}
                  text={boxWithPrefixMock}
                  align="left"
                  isFullWidth={false}
                />
              </div>
            </div>
          ) : (
            <Heading
              tag={HeadingTag.H4}
              size={HeadingSize.H4}
              text={pricingCopyMaybeWithPrefix}
              align="left"
              isFullWidth={false}
              classes={classNames('mt-auto pt-4', dividerClasses)}
            />
          ))}
      </div>
    </InternalLinkWrapper>
  );
}

export default DefaultCard;
